// Lifecycle stage label resolver.
//
// Resolution order (per .forge/plans/v2-stage-primitives.md §1.1):
//   1. Consolidated CHG-1132 vocab YAML — ONLY when shipped + bundled
//      (this CHG, CHG-1179 / ITER-3293, declares the contract; CHG-1132 is in
//      flight in a parallel team. When their YAML lands, a follow-up CHG swaps
//      the primary source from STAGE_LABELS_FALLBACK to the loaded YAML).
//   2. STAGE_LABELS_FALLBACK from per-domain seeds (label + description).
//      tone_color + severity synthesized via heuristics.
//   3. Worst case: titleCase(stage_id) + g-settings + info.
//
// API surface is stable across the resolution chain — callers always get the
// same shape regardless of which source resolved.
//
// Per feedback_field_primitive_components_canonical.md (resolved-via-named-component
// pattern) and feedback_stage_labels_human_readable.md (no raw enum strings in JSX).

import {
  STAGE_LABELS_FALLBACK,
  type Entity,
  type StageSeed,
} from "./stage-labels-fallback";
import {
  heuristicTone,
  heuristicSeverity,
  normalizeStageId,
  type ToneToken,
  type Severity,
} from "./stage-tone-heuristic";

export type { Entity, ToneToken, Severity };

export interface StageLabel {
  label: string;
  microcopy: string;
  tone_color: ToneToken;
  severity: Severity;
}

export interface VocabPayload {
  label?: string;
  microcopy?: string;
  tone_color?: ToneToken;
  severity?: Severity;
}

// Module-scoped vocab override. When CHG-1132 lands, the follow-up CHG will
// populate this from the bundled YAML at module init. Until then it stays
// null and the fallback chain handles every lookup.
let vocabOverride: Partial<Record<Entity, Record<string, VocabPayload>>> | null = null;

/**
 * Inject a vocab map (test seam + CHG-1132 swap point). Pass `null` to clear.
 * Production code does not call this; CHG-1132's follow-up will call once at
 * module init from the bundled YAML.
 */
export function setVocabOverride(
  payload: Partial<Record<Entity, Record<string, VocabPayload>>> | null,
): void {
  vocabOverride = payload;
}

function titleCase(s: string): string {
  return s
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function interpolate(
  template: string,
  placeholders: Record<string, string | number> | undefined,
): string {
  if (!template || !placeholders) return template;
  return template.replace(/\{(\w+)\}/g, (whole, key) => {
    const v = placeholders[key];
    return v === undefined || v === null ? whole : String(v);
  });
}

/**
 * Resolve a (entity, stage_id) tuple to its v2 display payload. Never throws;
 * always returns a usable shape.
 *
 * @param stage_id — accepts kebab-case ('at-risk') or snake_case ('at_risk')
 * @param entity   — campaign | donor | pledge | recurring
 * @param placeholders — optional {key: value} for {key} substitution in microcopy
 */
export function getStageLabel(
  stage_id: string,
  entity: Entity,
  placeholders?: Record<string, string | number>,
): StageLabel {
  const norm = normalizeStageId(stage_id);

  // 1. Vocab override (CHG-1132 forward-promise).
  const vocabEntry = vocabOverride?.[entity]?.[norm];
  if (vocabEntry?.label) {
    return {
      label: vocabEntry.label,
      microcopy: interpolate(vocabEntry.microcopy ?? "", placeholders),
      tone_color: vocabEntry.tone_color ?? heuristicTone(norm),
      severity: vocabEntry.severity ?? heuristicSeverity(norm),
    };
  }

  // 2. Per-domain fallback seed.
  const seed: StageSeed | undefined = STAGE_LABELS_FALLBACK[entity]?.[norm];
  if (seed) {
    return {
      label: seed.label,
      microcopy: "", // seed has no microcopy template; leave empty
      tone_color: heuristicTone(norm),
      severity: heuristicSeverity(norm),
    };
  }

  // 3. Worst case.
  return {
    label: titleCase(norm),
    microcopy: "",
    tone_color: "g-settings",
    severity: "info",
  };
}
